import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Upload, Trash2, ArrowLeft, Eye, EyeOff, ChevronUp, ChevronDown, ArrowLeftRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function compressImage(file, maxWidth = 1200, quality = 0.75) {
  return new Promise((resolve) => {
    // If file is small enough or unsupported format, send as-is
    if (file.size < 2 * 1024 * 1024) return resolve(file);
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const ratio = Math.min(maxWidth / img.width, 1);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          if (!blob) return resolve(file);
          const fileName = file.name.replace(/\.(heic|heif|tiff|bmp)$/i, ".jpg");
          resolve(new File([blob], fileName, { type: "image/jpeg" }));
        }, "image/jpeg", quality);
      } catch (err) {
        URL.revokeObjectURL(url);
        resolve(file);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file); // Fallback: backend will handle compression via Pillow
    };
    img.src = url;
  });
}

export default function AdminBeforeAfter() {
  const { user, token, loading: authLoading, authHeaders } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [label, setLabel] = useState("");
  const [category, setCategory] = useState("");
  const [beforePreview, setBeforePreview] = useState(null);
  const [afterPreview, setAfterPreview] = useState(null);

  useEffect(() => { if (!authLoading && !user) navigate("/admin/login"); }, [user, authLoading, navigate]);
  useEffect(() => { if (token) fetchItems(); }, [token]);

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/before-after`);
      setItems(res.data);
    } catch (e) { console.error(e); }
  };

  const handleFileSelect = (file, type) => {
    if (!file) return;
    if (type === "before") {
      setBeforeFile(file);
      setBeforePreview(URL.createObjectURL(file));
    } else {
      setAfterFile(file);
      setAfterPreview(URL.createObjectURL(file));
    }
  };

  const upload = async () => {
    if (!beforeFile || !afterFile) return;
    setUploading(true);
    try {
      const compressedBefore = await compressImage(beforeFile);
      const compressedAfter = await compressImage(afterFile);
      const fd = new FormData();
      fd.append("before", compressedBefore);
      fd.append("after", compressedAfter);
      fd.append("label", label || "Renovation");
      fd.append("category", category);
      await axios.post(`${API}/before-after`, fd, { headers: { ...authHeaders, "Content-Type": "multipart/form-data" }, timeout: 60000 });
      setBeforeFile(null); setAfterFile(null); setLabel(""); setCategory("");
      setBeforePreview(null); setAfterPreview(null);
      fetchItems();
    } catch (e) { console.error(e); alert("Erreur upload. Verifiez la taille des images."); }
    finally { setUploading(false); }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Supprimer cette paire avant/apres ?")) return;
    try { await axios.delete(`${API}/before-after/${id}`, { headers: authHeaders }); fetchItems(); } catch (e) { console.error(e); }
  };

  const swapImages = async (item) => {
    try { await axios.put(`${API}/before-after/${item.id}`, { before_url: item.after_url, after_url: item.before_url, before_public_id: item.after_public_id || "", after_public_id: item.before_public_id || "" }, { headers: authHeaders }); fetchItems(); } catch (e) { console.error(e); }
  };

  const toggleActive = async (item) => {
    try { await axios.put(`${API}/before-after/${item.id}`, { active: !item.active }, { headers: authHeaders }); fetchItems(); } catch (e) { console.error(e); }
  };

  const reorder = async (item, direction) => {
    const idx = items.findIndex(i => i.id === item.id);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= items.length) return;
    try {
      await axios.put(`${API}/before-after/${item.id}`, { order: items[swapIdx].order }, { headers: authHeaders });
      await axios.put(`${API}/before-after/${items[swapIdx].id}`, { order: item.order }, { headers: authHeaders });
      fetchItems();
    } catch (e) { console.error(e); }
  };

  if (authLoading) return <div style={{ minHeight: '100vh', backgroundColor: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><p style={{ color: '#aaa' }}>Chargement...</p></div>;
  if (!user) return null;

  return (
    <div data-testid="admin-before-after" style={{ minHeight: '100vh', backgroundColor: '#0A0A0A', padding: '32px' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button onClick={() => navigate("/admin/dashboard")} style={{ color: '#888', background: 'none', border: 'none', cursor: 'pointer' }}><ArrowLeft size={20} /></button>
            <h1 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '24px', fontWeight: 900, color: '#fff', margin: 0 }}>Avant / Apres</h1>
          </div>
          <span style={{ color: '#666', fontSize: '13px' }}>{items.length} paires</span>
        </div>

        {/* Upload Form */}
        <div style={{ backgroundColor: '#141414', border: '1px solid #27272A', borderRadius: '10px', padding: '24px', marginBottom: '24px' }}>
          <h3 style={{ color: '#fff', fontSize: '15px', fontWeight: 700, marginBottom: '16px' }}>Ajouter une paire</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            {/* Before */}
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#8B0000', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px' }}>Photo AVANT</label>
              {beforePreview ? (
                <div style={{ position: 'relative' }}>
                  <img src={beforePreview} alt="Avant" style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', borderRadius: '8px', border: '2px solid #333' }} />
                  <button onClick={() => { setBeforeFile(null); setBeforePreview(null); }} style={{ position: 'absolute', top: '8px', right: '8px', backgroundColor: '#dc2626', color: '#fff', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Trash2 size={12} /></button>
                </div>
              ) : (
                <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', aspectRatio: '4/3', border: '2px dashed #333', borderRadius: '8px', cursor: 'pointer', backgroundColor: '#0a0a0a' }}>
                  <Upload size={24} color="#666" />
                  <span style={{ color: '#666', fontSize: '12px', marginTop: '8px' }}>Cliquez pour uploader</span>
                  <input type="file" accept="image/*" onChange={e => handleFileSelect(e.target.files[0], "before")} style={{ display: 'none' }} />
                </label>
              )}
            </div>
            {/* After */}
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px' }}>Photo APRES</label>
              {afterPreview ? (
                <div style={{ position: 'relative' }}>
                  <img src={afterPreview} alt="Apres" style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', borderRadius: '8px', border: '2px solid #166534' }} />
                  <button onClick={() => { setAfterFile(null); setAfterPreview(null); }} style={{ position: 'absolute', top: '8px', right: '8px', backgroundColor: '#dc2626', color: '#fff', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Trash2 size={12} /></button>
                </div>
              ) : (
                <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', aspectRatio: '4/3', border: '2px dashed #333', borderRadius: '8px', cursor: 'pointer', backgroundColor: '#0a0a0a' }}>
                  <Upload size={24} color="#666" />
                  <span style={{ color: '#666', fontSize: '12px', marginTop: '8px' }}>Cliquez pour uploader</span>
                  <input type="file" accept="image/*" onChange={e => handleFileSelect(e.target.files[0], "after")} style={{ display: 'none' }} />
                </label>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <input type="text" placeholder="Titre (ex: Salle de bain)" value={label} onChange={e => setLabel(e.target.value)}
              style={{ flex: 1, backgroundColor: '#0a0a0a', border: '1px solid #27272A', color: '#fff', padding: '10px 14px', borderRadius: '6px', fontSize: '14px' }} />
            <select value={category} onChange={e => setCategory(e.target.value)}
              style={{ backgroundColor: '#0a0a0a', border: '1px solid #27272A', color: '#fff', padding: '10px 14px', borderRadius: '6px', fontSize: '14px' }}>
              <option value="">Categorie</option>
              <option value="Salle de bain">Salle de bain</option>
              <option value="Cuisine">Cuisine</option>
              <option value="Salon">Salon</option>
              <option value="Chambre">Chambre</option>
              <option value="Couloir">Couloir</option>
              <option value="Facade">Facade</option>
              <option value="Appartement">Appartement</option>
              <option value="Maison">Maison</option>
            </select>
          </div>
          <button onClick={upload} disabled={!beforeFile || !afterFile || uploading}
            style={{ backgroundColor: (!beforeFile || !afterFile || uploading) ? '#333' : '#8B0000', color: '#fff', fontWeight: 700, fontSize: '13px', padding: '12px 24px', border: 'none', borderRadius: '6px', cursor: (!beforeFile || !afterFile || uploading) ? 'default' : 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {uploading ? "Upload en cours..." : "Ajouter la paire"}
          </button>
        </div>

        {/* List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {items.map((item, i) => (
            <div key={item.id} style={{ backgroundColor: '#141414', border: '1px solid #27272A', borderRadius: '10px', padding: '16px', opacity: item.active ? 1 : 0.5 }}>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
                  <img src={item.before_url} alt="Avant" style={{ width: '120px', height: '80px', objectFit: 'cover', borderRadius: '6px', border: '2px solid #333' }} />
                  <img src={item.after_url} alt="Apres" style={{ width: '120px', height: '80px', objectFit: 'cover', borderRadius: '6px', border: '2px solid #166534' }} />
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '4px' }}>
                    <span style={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>{item.label}</span>
                    {item.category && <span style={{ color: '#8B0000', fontSize: '12px', fontWeight: 600 }}>{item.category}</span>}
                    {!item.active && <span style={{ color: '#888', fontSize: '11px' }}>Masque</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button onClick={() => reorder(item, "up")} disabled={i === 0} style={{ color: '#666', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', opacity: i === 0 ? 0.3 : 1 }}><ChevronUp size={16} /></button>
                  <button onClick={() => reorder(item, "down")} disabled={i === items.length - 1} style={{ color: '#666', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', opacity: i === items.length - 1 ? 0.3 : 1 }}><ChevronDown size={16} /></button>
                  <button onClick={() => swapImages(item)} title="Inverser avant / après" data-testid={`ba-swap-${i}`} style={{ color: '#f59e0b', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}><ArrowLeftRight size={16} /></button>
                  <button onClick={() => toggleActive(item)} style={{ color: item.active ? '#4ade80' : '#666', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>{item.active ? <Eye size={16} /> : <EyeOff size={16} />}</button>
                  <button onClick={() => deleteItem(item.id)} style={{ color: '#666', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && <p style={{ color: '#666', textAlign: 'center', padding: '40px' }}>Aucune paire avant/apres. Ajoutez-en une ci-dessus.</p>}
        </div>
      </div>
    </div>
  );
}
